import {
	type FrontendProduct,
	itemsAreSame,
	type ProductItem,
	type ProductV2,
	productsAreSame,
	productV2ToFeatureItems,
} from "@autumn/shared";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
} from "react";
import {
	disabledItemDraftController,
	type ItemDraftController,
} from "@/hooks/inline-editor/useItemDraftController";
import { useFeaturesQuery } from "@/hooks/queries/useFeaturesQuery";
import { useProductStore } from "@/hooks/stores/useProductStore";
import { useSheetStore } from "@/hooks/stores/useSheetStore";
import { getItemId } from "@/utils/product/productItemUtils";

interface ProductContextValue {
	product: FrontendProduct;
	setProduct: (
		product: FrontendProduct | ((prev: FrontendProduct) => FrontendProduct),
	) => void;
	initialProduct?: FrontendProduct;
	sheetType: string | null;
	itemId: string | null;
	initialItem: ProductItem | null;
	setSheet: (params: { type: string | null; itemId?: string | null }) => void;
	setInitialItem: (item: ProductItem | null) => void;
	updateItemId: (itemId: string) => void;
	closeSheet: () => void;
	itemDraft: ItemDraftController;
}

const ProductContext = createContext<ProductContextValue | null>(null);

/**
 * Provider that allows overriding the product and sheet state source.
 * When wrapped with this provider, child components will use the provided
 * values instead of the Zustand stores.
 */
export function ProductProvider({
	children,
	product,
	setProduct,
	initialProduct,
	sheetType,
	itemId,
	initialItem,
	setSheet,
	setInitialItem,
	updateItemId,
	closeSheet,
	itemDraft,
}: {
	children: ReactNode;
	product: FrontendProduct;
	setProduct: (
		product: FrontendProduct | ((prev: FrontendProduct) => FrontendProduct),
	) => void;
	initialProduct?: FrontendProduct;
	sheetType: string | null;
	itemId: string | null;
	initialItem: ProductItem | null;
	setSheet: (params: { type: string | null; itemId?: string | null }) => void;
	setInitialItem: (item: ProductItem | null) => void;
	updateItemId: (itemId: string) => void;
	closeSheet: () => void;
	itemDraft: ItemDraftController;
}) {
	return (
		<ProductContext.Provider
			value={{
				product,
				setProduct,
				initialProduct,
				sheetType,
				itemId,
				initialItem,
				setSheet,
				setInitialItem,
				updateItemId,
				closeSheet,
				itemDraft,
			}}
		>
			{children}
		</ProductContext.Provider>
	);
}

/** Hook to get product and setProduct. Uses context if available, otherwise Zustand. */
export function useProduct() {
	const context = useContext(ProductContext);
	const storeProduct = useProductStore((s) => s.product);
	const storeSetProduct = useProductStore((s) => s.setProduct);

	if (context) {
		return {
			product: context.product,
			setProduct: context.setProduct,
			initialProduct: context.initialProduct,
		};
	}

	return {
		product: storeProduct,
		setProduct: storeSetProduct,
		initialProduct: undefined,
	};
}

/** Hook to get sheet state and actions. Uses context if available, otherwise Zustand. */
export function useSheet() {
	const context = useContext(ProductContext);
	const storeSheetType = useSheetStore((s) => s.type);
	const storeItemId = useSheetStore((s) => s.itemId);
	const storeInitialItem = useSheetStore((s) => s.initialItem);
	const storeSetSheet = useSheetStore((s) => s.setSheet);
	const storeSetInitialItem = useSheetStore((s) => s.setInitialItem);
	const storeCloseSheet = useSheetStore((s) => s.closeSheet);

	const storeUpdateItemId = useSheetStore((s) => s.updateItemId);

	if (context) {
		return {
			sheetType: context.sheetType,
			itemId: context.itemId,
			initialItem: context.initialItem,
			setSheet: context.setSheet,
			setInitialItem: context.setInitialItem,
			updateItemId: context.updateItemId,
			closeSheet: context.closeSheet,
			itemDraft: context.itemDraft,
		};
	}

	return {
		sheetType: storeSheetType,
		itemId: storeItemId,
		initialItem: storeInitialItem,
		setSheet: storeSetSheet,
		setInitialItem: storeSetInitialItem,
		updateItemId: storeUpdateItemId,
		closeSheet: storeCloseSheet,
		itemDraft: disabledItemDraftController,
	};
}

/** Hook to get current item being edited. Uses context if available, otherwise Zustand. */
export function useCurrentItem() {
	const context = useContext(ProductContext);
	const { product } = useProduct();
	const { itemId } = useSheet();

	return useMemo(() => {
		if (!itemId || !product?.items) return null;
		if (
			context?.itemDraft.session &&
			context.itemDraft.session.itemId === itemId
		) {
			return context.itemDraft.session.draftItem;
		}

		const featureItems = productV2ToFeatureItems({ items: product.items });

		for (let i = 0; i < product.items.length; i++) {
			const item = product.items[i];
			if (!item) continue;

			const isFeatureItem = featureItems.some((fi) => fi === item);
			if (!isFeatureItem) continue;

			const currentItemId = getItemId({ item, itemIndex: i });
			if (currentItemId === itemId) {
				return item;
			}
		}

		return null;
	}, [context?.itemDraft.session, product, itemId]);
}

/** Hook to set the current item being edited. Uses context if available, otherwise Zustand. */
export function useSetCurrentItem() {
	const { product, setProduct } = useProduct();
	const { itemId, itemDraft, updateItemId } = useSheet();

	return useCallback(
		(updatedItem: ProductItem) => {
			if (itemDraft.session && itemDraft.session.itemId === itemId) {
				itemDraft.updateItem({ item: updatedItem });
				return;
			}

			if (!product || !product.items || !itemId) return;

			let originalIndex = -1;
			for (let i = 0; i < product.items.length; i++) {
				const item = product.items[i];
				if (!item) continue;

				const currentItemId = getItemId({ item, itemIndex: i });
				if (currentItemId === itemId) {
					originalIndex = i;
					break;
				}
			}

			if (originalIndex === -1) return;

			const newItemId = getItemId({
				item: updatedItem,
				itemIndex: originalIndex,
			});
			if (newItemId !== itemId) {
				updateItemId(newItemId);
			}

			const updatedItems = [...product.items];
			updatedItems[originalIndex] = updatedItem;
			setProduct({ ...product, items: updatedItems });
		},
		[itemDraft, itemId, product, setProduct, updateItemId],
	);
}

/** Hook to check if the current item has unsaved changes. Uses context if available, otherwise Zustand. */
export function useHasItemChanges() {
	const item = useCurrentItem();
	const { initialItem, itemDraft } = useSheet();
	const { features = [] } = useFeaturesQuery();

	return useMemo(() => {
		if (itemDraft.session) {
			const { same } = itemsAreSame({
				item1: itemDraft.session.draftItem,
				item2: itemDraft.session.initialItem,
				features,
			});

			return !same;
		}

		if (!item || !initialItem) {
			return false;
		}

		const { same } = itemsAreSame({
			item1: item,
			item2: initialItem,
			features,
		});

		return !same;
	}, [itemDraft.session, item, initialItem, features]);
}

/** Hook to discard item changes (restore to initial state) and close the sheet. Uses context if available, otherwise Zustand. */
export function useDiscardItemAndClose() {
	const setCurrentItem = useSetCurrentItem();
	const { initialItem, closeSheet, itemDraft } = useSheet();

	return useCallback(() => {
		if (itemDraft.session) {
			itemDraft.discardItem();
			closeSheet();
			return;
		}

		if (initialItem) {
			setCurrentItem(initialItem);
		}
		closeSheet();
	}, [itemDraft, initialItem, setCurrentItem, closeSheet]);
}

/** Hook to check if the product has unsaved changes compared to initial state. Only works in context mode. */
export function useHasPlanChanges() {
	const context = useContext(ProductContext);
	const { product, initialProduct } = useProduct();
	const { features = [] } = useFeaturesQuery();

	return useMemo(() => {
		if (context?.itemDraft.enabled) {
			return context.itemDraft.isDirtySupported;
		}

		if (!initialProduct) return false;

		const { itemsSame, freeTrialsSame } = productsAreSame({
			newProductV2: product as unknown as ProductV2,
			curProductV2: initialProduct as unknown as ProductV2,
			features,
		});

		const versionsSame = product.version === initialProduct.version;

		return !(itemsSame && freeTrialsSame && versionsSame);
	}, [context?.itemDraft, product, initialProduct, features]);
}
